import {getStoreTheme} from "stores/theme"
import {
  AppStoreThemeSchema,
  QueryStringThemeSchema,
  ThemeSourceTypeEnum,
  ThemeTypeEnum,
} from "./data"

function getThemeFromQueryString() {
  const searchParams = new URLSearchParams(window.location.search)
  return QueryStringThemeSchema.parse(searchParams.get("theme"))
}

function getThemeFromStore() {
  return AppStoreThemeSchema.parse(getStoreTheme())
}

/**
 *
 * @returns {import("./data").ThemeType}
 */
function getThemeFromSystemPerference() {
  if (!window) return ThemeTypeEnum.enum.light

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? ThemeTypeEnum.enum.dark
    : ThemeTypeEnum.enum.light
}

/**
 * Validates if a given value is a valid theme type.
 *
 * @param {unknown} val - The value to validate.
 * @returns {val is import("./data").ThemeType}
 *
 * @example
 * isValidTheme("dark"); // true
 * isValidTheme("light"); // true
 * isValidTheme("blue"); // false
 * isValidTheme(null); // false
 * isValidTheme(""); // false
 */
function isValidTheme(val) {
  return ThemeTypeEnum.safeParse(val).success
}

/**
 * Determines the current theme by checking various sources in a prioritized order.
 *
 * The theme is resolved in the following order:
 * 1. Query string (`getThemeFromQueryString`).
 * 2. Application store (`getThemeFromStore`).
 * 3. System preference (`getThemeFromSystemPerference`).
 *
 * If a theme is found in the query string or store and is valid (using `isValidTheme`), it is returned.
 * Otherwise, the system preference is used as the fallback.
 *
 * @example
 * // Assuming system preference is set to "light":
 * determineTheme(); // "light" (if no valid theme in query string or store)
 */
export const determineTheme = () => {
  const themeFromQs = getThemeFromQueryString()
  const themeFromStore = getThemeFromStore()
  const themeFromSystem = getThemeFromSystemPerference()

  if (isValidTheme(themeFromQs)) {
    return {
      theme: themeFromQs,
      sourceType: ThemeSourceTypeEnum.enum.QueryString,
    }
  } else if (isValidTheme(themeFromStore)) {
    return {
      theme: themeFromStore,
      sourceType: ThemeSourceTypeEnum.enum.AppStore,
    }
  } else {
    return {theme: themeFromSystem, sourceType: ThemeSourceTypeEnum.enum.System}
  }
}
